import { h } from 'preact';
import { route } from 'preact-router';
import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Modal from '../components/shared/Modal.jsx';
import Button from '../components/shared/Button.jsx';
import { parseServerDate } from '../utils/dates.js';
import { translateError } from '../i18n.js';
import AiAssist from '../components/shared/AiAssist.jsx';
import DepartmentLabels from '../components/shared/DepartmentLabel.jsx';

// ── Helpers ───────────────────────────────────────────────────────────────────

const NL_MONTHS = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];
const NL_DAYS_SHORT = ['ma','di','wo','do','vr','za','zo'];
const NL_DAYS_LONG  = ['maandag','dinsdag','woensdag','donderdag','vrijdag','zaterdag','zondag'];

function fmtDate(d) {
  const dt = new Date(d);
  return `${dt.getDate()} ${NL_MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}
function fmtTime(d) {
  const dt = new Date(d);
  return dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}
function fmtDateTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
}
function toInputVal(d) {
  if (!d) return '';
  const dt = new Date(d);
  const pad = n => String(n).padStart(2,'0');
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}
function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0,0,0,0);
  return d;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// ── RecurrenceEditor ─────────────────────────────────────────────────────────

const RRULE_DAYS = [
  { key:'MO', label:'Ma' }, { key:'TU', label:'Di' }, { key:'WE', label:'Wo' },
  { key:'TH', label:'Do' }, { key:'FR', label:'Vr' }, { key:'SA', label:'Za' }, { key:'SU', label:'Zo' },
];

function buildRRule(freq, byDay, monthType, lastDayOfWeek) {
  if (!freq) return '';
  if (freq === 'DAILY')   return 'FREQ=DAILY';
  if (freq === 'YEARLY')  return 'FREQ=YEARLY';
  if (freq === 'WEEKLY')  return byDay.length ? `FREQ=WEEKLY;BYDAY=${byDay.join(',')}` : 'FREQ=WEEKLY';
  if (freq === 'MONTHLY') return monthType === 'lastX' ? `FREQ=MONTHLY;BYDAY=-1${lastDayOfWeek}` : 'FREQ=MONTHLY';
  return '';
}

function parseRRule(rrule) {
  if (!rrule) return { freq:'', byDay:[], monthType:'dom', lastDayOfWeek:'MO' };
  const parts = {};
  rrule.split(';').forEach(p => { const [k,v] = p.split('='); parts[k] = v; });
  const freq = parts.FREQ || '';
  const raw  = parts.BYDAY ? parts.BYDAY.split(',') : [];
  const isLastX = raw.length === 1 && /^-1/.test(raw[0]);
  return {
    freq,
    byDay: isLastX ? [] : raw,
    monthType: isLastX ? 'lastX' : 'dom',
    lastDayOfWeek: isLastX ? raw[0].slice(2) : 'MO',
  };
}

function RecurrenceEditor({ rrule, rruleUntil, onChange, startDate }) {
  const p = parseRRule(rrule);
  const [freq, setFreq]           = useState(p.freq);
  const [byDay, setByDay]         = useState(p.byDay);
  const [monthType, setMonthType] = useState(p.monthType);
  const [lastDay, setLastDay]     = useState(p.lastDayOfWeek);
  const [until, setUntil]         = useState(rruleUntil ? rruleUntil.slice(0,10) : '');

  function emit(f, d, mt, ld, u) { onChange(buildRRule(f, d, mt, ld) || null, u || null); }

  const domLabel = startDate ? (() => { const d = new Date(startDate); return `${d.getDate()}e van de maand`; })() : 'dag van de maand';

  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <div style="display:flex;gap:6px;flex-wrap:wrap">
        {[['','Eenmalig'],['DAILY','Dagelijks'],['WEEKLY','Wekelijks'],['MONTHLY','Maandelijks'],['YEARLY','Jaarlijks']].map(([val,lbl]) => (
          <button key={val} type="button" onClick={() => { setFreq(val); emit(val,byDay,monthType,lastDay,until); }}
            style={`padding:4px 11px;border-radius:14px;border:1px solid ${freq===val?'var(--color-primary)':'var(--color-border)'};background:${freq===val?'var(--color-primary)':'var(--color-surface)'};color:${freq===val?'#fff':'var(--color-text)'};font-size:12px;font-weight:600;cursor:pointer`}>
            {lbl}
          </button>
        ))}
      </div>
      {freq === 'WEEKLY' && (
        <div style="display:flex;gap:5px">
          {RRULE_DAYS.map(d => (
            <button key={d.key} type="button" onClick={() => { const nd = byDay.includes(d.key)?byDay.filter(x=>x!==d.key):[...byDay,d.key]; setByDay(nd); emit(freq,nd,monthType,lastDay,until); }}
              style={`width:30px;height:30px;border-radius:50%;border:1px solid ${byDay.includes(d.key)?'var(--color-primary)':'var(--color-border)'};background:${byDay.includes(d.key)?'var(--color-primary)':'var(--color-surface)'};color:${byDay.includes(d.key)?'#fff':'var(--color-text)'};font-size:10px;font-weight:700;cursor:pointer`}>
              {d.label}
            </button>
          ))}
        </div>
      )}
      {freq === 'MONTHLY' && (
        <div style="display:flex;flex-direction:column;gap:8px">
          <label style={`display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;padding:8px 10px;border-radius:8px;border:2px solid ${monthType==='dom'?'var(--color-primary)':'var(--color-border)'}`}>
            <input type="radio" checked={monthType==='dom'} onChange={() => { setMonthType('dom'); emit(freq,byDay,'dom',lastDay,until); }} />
            <span>📅 {domLabel} <span style="color:var(--color-text-muted);font-size:11px">(vaste datum)</span></span>
          </label>
          <label style={`display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;padding:8px 10px;border-radius:8px;border:2px solid ${monthType==='lastX'?'var(--color-primary)':'var(--color-border)'}`}>
            <input type="radio" checked={monthType==='lastX'} onChange={() => { setMonthType('lastX'); emit(freq,byDay,'lastX',lastDay,until); }} />
            <span style="display:flex;align-items:center;gap:6px">
              📅 Laatste
              <select class="form-input" value={lastDay} onClick={e=>e.stopPropagation()} onChange={e => { setLastDay(e.target.value); emit(freq,byDay,'lastX',e.target.value,until); }} style="padding:2px 6px;font-size:12px;width:auto">
                {RRULE_DAYS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
              van de maand
            </span>
          </label>
        </div>
      )}
      {freq && (
        <div style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;background:var(--color-bg-secondary)">
          <label style="font-size:12px;font-weight:600;white-space:nowrap">Herhalen t/m</label>
          <input class="form-input" type="date" value={until} onInput={e => { setUntil(e.target.value); emit(freq,byDay,monthType,lastDay,e.target.value); }} style="padding:4px 8px;font-size:12px;max-width:160px" />
          {until
            ? <button type="button" onClick={() => { setUntil(''); emit(freq,byDay,monthType,lastDay,''); }} style="background:none;border:none;cursor:pointer;color:var(--color-text-muted);font-size:16px;line-height:1">×</button>
            : <span style="font-size:11px;color:var(--color-text-muted)">Laat leeg voor eindeloos</span>
          }
        </div>
      )}
    </div>
  );
}

// ── ReminderPicker ────────────────────────────────────────────────────────────

const REMINDER_PRESETS = [
  { value:-10,   label:'10 min voor' },
  { value:-30,   label:'30 min voor' },
  { value:-60,   label:'1 uur voor'  },
  { value:-1440, label:'1 dag voor'  },
];

function ReminderPicker({ value, onChange }) {
  const [addMins, setAddMins] = useState('-30');
  const [addType, setAddType] = useState('in_app');

  function add() {
    const mins = Number(addMins);
    if (value.some(r => r.trigger_minutes === mins && r.type === addType)) return;
    onChange([...value, { trigger_minutes: mins, type: addType }]);
  }

  function labelFor(r) {
    const p = REMINDER_PRESETS.find(x => x.value === r.trigger_minutes);
    const t = p ? p.label : `${Math.abs(r.trigger_minutes)} min voor`;
    return `${r.type === 'email' ? '📧' : '🔔'} ${t}`;
  }

  return (
    <div>
      {value.length > 0 && (
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px">
          {value.map((r,i) => (
            <span key={i} style="display:flex;align-items:center;gap:4px;padding:3px 8px;border-radius:12px;background:var(--color-bg-secondary);border:1px solid var(--color-border);font-size:12px">
              {labelFor(r)}
              <button onClick={() => onChange(value.filter((_,j)=>j!==i))} style="background:none;border:none;cursor:pointer;padding:0 2px;color:var(--color-text-muted);font-size:14px;line-height:1">×</button>
            </span>
          ))}
        </div>
      )}
      <div style="display:flex;gap:6px;align-items:center">
        <select class="form-input" value={addMins} onChange={e=>setAddMins(e.target.value)} style="flex:1;padding:6px 8px;font-size:12px">
          {REMINDER_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select class="form-input" value={addType} onChange={e=>setAddType(e.target.value)} style="flex:0 0 auto;padding:6px 8px;font-size:12px">
          <option value="in_app">🔔 In-app</option>
          <option value="email">📧 E-mail</option>
        </select>
        <button type="button" onClick={add} style="padding:6px 12px;border-radius:6px;border:none;background:var(--color-primary);color:#fff;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap">+ Voeg toe</button>
      </div>
    </div>
  );
}

// ── RecurrenceScopeDialog ─────────────────────────────────────────────────────

function RecurrenceScopeDialog({ mode, onSelect, onClose }) {
  const [selected, setSelected] = useState('this');
  const opts = [
    { scope:'this',             label:'Alleen dit event',         desc:'Wijzig alleen deze ene occurrence.' },
    { scope:'this_and_future',  label:'Dit en alle volgende',     desc:'Wijzig dit event en alle toekomstige in de reeks.' },
    { scope:'all',              label:'Alle events in de reeks',  desc:'Wijzig alle events in de hele reeks.' },
  ];
  return (
    <Modal isOpen title={`Herhalend event ${mode === 'delete' ? 'verwijderen' : 'bewerken'}`} onClose={onClose} style="max-width:420px">
      <p style="font-size:13px;color:var(--color-text-muted);margin-bottom:16px">
        Dit is een herhalend event. Wat wil je {mode === 'delete' ? 'verwijderen' : 'bewerken'}?
      </p>
      <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:20px">
        {opts.map(opt => (
          <label key={opt.scope} style={`display:flex;align-items:flex-start;gap:10px;padding:10px 12px;border-radius:8px;border:2px solid ${selected===opt.scope?'var(--color-primary)':'var(--color-border)'};cursor:pointer`}>
            <input type="radio" name="rec_scope" value={opt.scope} checked={selected===opt.scope} onChange={() => setSelected(opt.scope)} style="margin-top:3px" />
            <div>
              <div style="font-weight:600;font-size:13px">{opt.label}</div>
              <div style="font-size:11px;color:var(--color-text-muted)">{opt.desc}</div>
            </div>
          </label>
        ))}
      </div>
      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose}>Annuleren</Button>
        <Button variant={mode==='delete'?'danger':'primary'} onClick={() => onSelect(selected)}>
          {mode === 'delete' ? 'Verwijderen' : 'Bewerken'}
        </Button>
      </div>
    </Modal>
  );
}

// ── InvitePicker ──────────────────────────────────────────────────────────────

// inviteScope: GET /api/calendar/invite-scope. A role for some departments invites only
// their sections and commissions (and members); not "Iedereen". The current invites keep
// their labels from the full lists.
function InvitePicker({ value, onChange, users, sections, commissions, inviteScope }) {
  const limited = inviteScope && !inviteScope.all;
  const [addScope, setAddScope] = useState('');

  function addEntry(scope, id) {
    const entry = { scope, scope_id: scope === 'iedereen' ? null : Number(id) };
    if (value.some(v => v.scope === entry.scope && v.scope_id === entry.scope_id)) return;
    onChange([...value, entry]);
  }

  function handleScopeChange(scope) {
    setAddScope(scope);
    if (scope === 'iedereen') {
      addEntry('iedereen', null);
      setAddScope('');
    }
  }

  function handleIdChange(id) {
    if (!id) return;
    addEntry(addScope, id);
    setAddScope('');
  }

  function remove(idx) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function labelFor(entry) {
    if (entry.scope === 'iedereen') return '👥 Iedereen';
    if (entry.scope === 'gebruiker') {
      const u = users.find(u => u.id === entry.scope_id);
      return `👤 ${u?.display_name || entry.scope_id}`;
    }
    if (entry.scope === 'section') {
      const s = sections.find(s => s.id === entry.scope_id);
      return `🏢 ${s?.name || entry.scope_id}`;
    }
    if (entry.scope === 'commission') {
      const c = commissions.find(c => c.id === entry.scope_id);
      return `📋 ${c?.name || entry.scope_id}`;
    }
    return entry.scope;
  }

  const idOptions = addScope === 'gebruiker' ? users
    : addScope === 'section' ? (limited ? sections.filter(s => inviteScope.section_ids.includes(s.id)) : sections)
    : addScope === 'commission' ? (limited ? commissions.filter(c => inviteScope.commission_ids.includes(c.id)) : commissions)
    : [];

  const placeholderLabel = addScope === 'gebruiker' ? 'persoon'
    : addScope === 'section' ? 'sectie'
    : 'commissie';

  return (
    <div>
      {/* Current invites */}
      {value.length > 0 && (
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px">
          {value.map((entry, i) => (
            <span key={i} style="display:flex;align-items:center;gap:4px;padding:3px 8px;border-radius:12px;background:color-mix(in srgb, var(--color-primary) 10%, transparent);border:1px solid color-mix(in srgb, var(--color-primary) 27%, transparent);font-size:12px;font-weight:500">
              {labelFor(entry)}
              <button onClick={() => remove(i)} style="background:none;border:none;cursor:pointer;padding:0 2px;color:var(--color-text-muted);font-size:14px;line-height:1">×</button>
            </span>
          ))}
        </div>
      )}
      {/* Add row */}
      <div style="display:flex;gap:6px;align-items:center">
        {!addScope ? (
          <select class="form-input" value="" onChange={e => handleScopeChange(e.target.value)} style="flex:0 0 140px;padding:6px 8px;font-size:13px">
            <option value="">+ Toevoegen</option>
            {!limited && <option value="iedereen">👥 Iedereen</option>}
            <option value="gebruiker">👤 Persoon</option>
            <option value="section">🏢 Sectie</option>
            <option value="commission">📋 Commissie</option>
          </select>
        ) : (
          <>
            <select class="form-input" value="" onChange={e => handleIdChange(e.target.value)} style="flex:1;padding:6px 8px;font-size:13px" autoFocus>
              <option value="">— Kies {placeholderLabel} —</option>
              {idOptions.map(o => <option key={o.id} value={o.id}>{o.name || o.display_name}</option>)}
            </select>
            <button onClick={() => setAddScope('')} style="padding:6px 10px;border-radius:6px;border:1px solid var(--color-border);background:var(--color-surface);cursor:pointer;font-size:12px;color:var(--color-text-muted)">✕</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── GroupsModal ───────────────────────────────────────────────────────────────

function GroupsModal({ onClose, onGroupsChanged }) {
  const [groups, setGroups]   = useState([]);
  const [users, setUsers]     = useState([]);
  const [editGroup, setEditGroup] = useState(null); // null | group object
  const [newName, setNewName] = useState('');
  const [saving, setSaving]   = useState(false);
  const [addUserId, setAddUserId] = useState('');

  useEffect(() => {
    api('/api/calendar/groups').then(setGroups).catch(err => console.error('Failed to load groups:', err));
    api('/api/calendar/users').then(setUsers).catch(err => console.error('Failed to load users:', err));
  }, []);

  async function createGroup() {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const g = await api('/api/calendar/groups', { method:'POST', body: JSON.stringify({ naam: newName.trim() }) });
      setGroups(gs => [...gs, g]);
      setNewName('');
      onGroupsChanged?.();
    } catch { alert('Aanmaken mislukt.'); }
    finally { setSaving(false); }
  }

  async function deleteGroup(id) {
    if (!confirm('Groep verwijderen?')) return;
    try {
      await api(`/api/calendar/groups/${id}`, { method:'DELETE' });
      setGroups(gs => gs.filter(g => g.id !== id));
      if (editGroup?.id === id) setEditGroup(null);
      onGroupsChanged?.();
    } catch { alert('Verwijderen mislukt.'); }
  }

  async function addMember(groupId) {
    if (!addUserId) return;
    try {
      const leden = await api(`/api/calendar/groups/${groupId}/members`, { method:'POST', body: JSON.stringify({ user_ids: [Number(addUserId)] }) });
      setGroups(gs => gs.map(g => g.id === groupId ? { ...g, leden, leden_count: leden.length } : g));
      if (editGroup?.id === groupId) setEditGroup(prev => ({ ...prev, leden }));
      setAddUserId('');
      onGroupsChanged?.();
    } catch { alert('Toevoegen mislukt.'); }
  }

  async function removeMember(groupId, userId) {
    try {
      await api(`/api/calendar/groups/${groupId}/members/${userId}`, { method:'DELETE' });
      setGroups(gs => gs.map(g => g.id === groupId ? { ...g, leden: g.leden.filter(l => l.id !== userId), leden_count: (g.leden_count||1)-1 } : g));
      if (editGroup?.id === groupId) setEditGroup(prev => ({ ...prev, leden: prev.leden.filter(l => l.id !== userId) }));
      onGroupsChanged?.();
    } catch { alert('Verwijderen mislukt.'); }
  }

  const activeGroup = editGroup ? groups.find(g => g.id === editGroup.id) : null;

  return (
    <Modal isOpen title="Mijn groepen" onClose={onClose} style="max-width:560px;width:95vw">
      <div style="display:flex;flex-direction:column;gap:16px">

        {/* Create new group */}
        <div style="display:flex;gap:8px">
          <input class="form-input" value={newName} onInput={e=>setNewName(e.target.value)} placeholder="Naam nieuwe groep" style="flex:1" onKeyDown={e=>e.key==='Enter'&&createGroup()} />
          <Button variant="primary" onClick={createGroup} disabled={saving || !newName.trim()}>Aanmaken</Button>
        </div>

        {groups.length === 0 && (
          <div style="text-align:center;color:var(--color-text-muted);padding:20px;font-size:13px">Nog geen groepen. Maak een groep aan om leden te bundelen.</div>
        )}

        {/* Groups list */}
        {groups.map(g => (
          <div key={g.id} style="border:1px solid var(--color-border);border-radius:10px;overflow:hidden">
            <div style="display:flex;align-items:center;gap:10px;padding:10px 14px;background:var(--color-bg-secondary)">
              <div style="flex:1">
                <div style="font-weight:600;font-size:14px">📋 {g.naam}</div>
                <div style="font-size:12px;color:var(--color-text-muted)">{g.leden_count || 0} leden</div>
              </div>
              <button onClick={() => setEditGroup(editGroup?.id === g.id ? null : g)} style="padding:4px 10px;border-radius:6px;border:1px solid var(--color-border);background:var(--color-surface);font-size:12px;cursor:pointer">
                {editGroup?.id === g.id ? 'Sluiten' : 'Beheren'}
              </button>
              <button onClick={() => deleteGroup(g.id)} style="padding:4px 8px;border-radius:6px;border:1px solid #fca5a5;background:#fee2e2;color:#dc2626;font-size:12px;cursor:pointer">🗑</button>
            </div>

            {editGroup?.id === g.id && (
              <div style="padding:12px 14px;display:flex;flex-direction:column;gap:10px">
                {/* Members list */}
                {(activeGroup?.leden || []).length === 0 && (
                  <div style="font-size:12px;color:var(--color-text-muted)">Nog geen leden in deze groep.</div>
                )}
                <div style="display:flex;flex-wrap:wrap;gap:6px">
                  {(activeGroup?.leden || []).map(l => (
                    <span key={l.id} style="display:flex;align-items:center;gap:4px;padding:3px 8px;border-radius:12px;background:var(--color-bg-secondary);border:1px solid var(--color-border);font-size:12px">
                      👤 {l.display_name}
                      <button onClick={() => removeMember(g.id, l.id)} style="background:none;border:none;cursor:pointer;padding:0 2px;color:var(--color-text-muted);font-size:14px;line-height:1">×</button>
                    </span>
                  ))}
                </div>
                {/* Add member */}
                <div style="display:flex;gap:6px">
                  <select class="form-input" value={addUserId} onChange={e=>setAddUserId(e.target.value)} style="flex:1;padding:6px 8px;font-size:13px">
                    <option value="">— Lid toevoegen —</option>
                    {users.filter(u => !(activeGroup?.leden||[]).find(l => l.id === u.id)).map(u => (
                      <option key={u.id} value={u.id}>{u.display_name}</option>
                    ))}
                  </select>
                  <button onClick={() => addMember(g.id)} disabled={!addUserId} style="padding:6px 12px;border-radius:6px;border:none;background:var(--color-primary);color:#fff;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap">Toevoegen</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div class="flex justify-end mt-4">
        <Button variant="secondary" onClick={onClose}>Sluiten</Button>
      </div>
    </Modal>
  );
}

// ── SubscribeModal ────────────────────────────────────────────────────────────
// The member's personal agenda link (routes/calendar.js /feed): added once to Outlook,
// Google or Apple Calendar, which then fetch the events themselves.

const fetchedLabel = at => at ? new Date(`${at.replace(' ', 'T')}Z`).toLocaleString('nl-NL', { day:'numeric', month:'long', hour:'2-digit', minute:'2-digit' }) : null;

function SubscribeModal({ onClose }) {
  const [feed, setFeed]       = useState(null);
  const [error, setError]     = useState(null);
  const [copied, setCopied]   = useState(false);
  const [busy, setBusy]       = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api('/api/calendar/feed').then(setFeed).catch(err => setError(err?.code === 'members_only' ? 'members_only' : 'failed'));
  }, []);

  // Only says "Gekopieerd" when it worked; otherwise the link stays selected to copy by hand
  async function copy() {
    let ok = false;
    try { await navigator.clipboard.writeText(feed.url); ok = true; }
    catch {
      const input = inputRef.current;
      input?.focus();
      input?.setSelectionRange(0, feed.url.length);
      try { ok = document.execCommand('copy'); } catch { ok = false; }
    }
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function reset() {
    if (!confirm('Een nieuwe link maken? De oude link werkt dan meteen niet meer, ook niet in agenda\'s waar je hem al hebt toegevoegd.')) return;
    setBusy(true);
    try { setFeed(await api('/api/calendar/feed/reset', { method:'POST' })); setCopied(false); }
    catch { alert('Nieuwe link maken mislukt.'); }
    finally { setBusy(false); }
  }

  const step = 'margin:4px 0 0;padding-left:20px;list-style:decimal;font-size:13px;color:var(--color-text-muted);line-height:1.6';
  return (
    <Modal isOpen title="Abonneren in je eigen agenda" onClose={onClose}>
      <div style="display:flex;flex-direction:column;gap:14px;font-size:14px;line-height:1.5">
        {error ? (
          <div style="color:var(--color-text-muted)">{error === 'members_only' ? 'Alleen leden van deze club hebben een agenda-link.' : 'De link kon niet worden geladen. Probeer het later opnieuw.'}</div>
        ) : !feed ? (
          <div style="color:var(--color-text-muted)">Laden...</div>
        ) : (<>
          <p style="margin:0">Zet je afspraken van deze club in Outlook, Google Agenda of de agenda op je telefoon. Nieuwe en gewijzigde afspraken komen vanzelf mee; je agenda-app kijkt een paar keer per dag of er iets veranderd is.</p>

          <a href={feed.webcalUrl} style="align-self:flex-start;padding:8px 14px;border-radius:8px;background:var(--color-primary);color:#fff;font-size:13px;font-weight:600;text-decoration:none">📅 Openen in agenda-app</a>

          <div>
            <div style="font-size:12px;font-weight:600;margin-bottom:4px">Of kopieer je persoonlijke link</div>
            <div style="display:flex;gap:6px">
              <input ref={inputRef} class="form-input" readOnly value={feed.url} onFocus={e => e.target.select()} style="flex:1;min-width:0;font-size:12px" />
              <Button variant="secondary" onClick={copy}>{copied ? 'Gekopieerd' : 'Kopiëren'}</Button>
            </div>
          </div>

          <div>
            {/* Why via the web: Outlook for Mac and the new Outlook for Windows can't subscribe,
                only import once; a subscription made on the web shows up in those apps too */}
            <div style="font-weight:600;font-size:13px">Outlook (ook op Mac en Windows)</div>
            <ol style={step}><li>Open Outlook in je browser (outlook.office.com of outlook.live.com) en ga naar Agenda.</li><li>Kies <b>Agenda toevoegen</b>, dan de optie om je te <b>abonneren via het web</b>, en plak de link.</li><li>Daarna verschijnt de agenda vanzelf in Outlook op je computer en telefoon. Zie je hem niet, start Outlook dan opnieuw.</li></ol>
            <div style="font-size:12px;color:var(--color-text-muted);margin-top:4px">Kies niet <b>ICS importeren</b>: dan komen de afspraken er één keer in en worden ze nooit bijgewerkt.</div>
            <div style="font-weight:600;font-size:13px;margin-top:8px">Google Agenda</div>
            <ol style={step}><li>Op een computer: klik bij <b>Andere agenda's</b> op het plusje.</li><li>Kies <b>Via URL</b> en plak de link. Daarna staat hij ook op je telefoon.</li></ol>
            <div style="font-weight:600;font-size:13px;margin-top:8px">Agenda van Apple (iPhone of Mac)</div>
            <ol style={step}><li>Tik op <b>Openen in agenda-app</b> en kies <b>Abonneer</b>. Gebruik je Outlook op je Mac, volg dan de stappen voor Outlook.</li></ol>
          </div>

          <div style="font-size:12px;color:var(--color-text-muted);background:var(--color-bg);border:1px solid var(--color-border);border-radius:8px;padding:10px 12px">
            Je agenda kan alleen lezen: aanmelden, afmelden en wijzigen doe je hier in de app. Afspraken waarvoor je je afmeldt, verdwijnen uit je agenda. Deel de link niet: wie hem heeft, ziet jouw afspraken.
            <div style="margin-top:6px">{feed.lastFetchedAt ? `Laatst opgehaald door een agenda: ${fetchedLabel(feed.lastFetchedAt)}.` : 'Nog niet opgehaald door een agenda.'}</div>
          </div>
        </>)}
      </div>
      <div class="flex justify-between mt-4" style="gap:8px;flex-wrap:wrap">
        {feed ? <Button variant="secondary" onClick={reset} disabled={busy}>Nieuwe link maken</Button> : <span />}
        <Button variant="primary" onClick={onClose}>Sluiten</Button>
      </div>
    </Modal>
  );
}

// ── EventFormModal ────────────────────────────────────────────────────────────

const BLANK_FORM_SHAPE = { titel:'', beschrijving:'', categorie_id:'', start_datetime:'', eind_datetime:'', hele_dag:false, locatie_tekst:'', rsvp_actief:false, max_deelnemers:'' };

function makeBlankForm() {
  const pad = n => String(n).padStart(2,'0');
  const now = new Date();
  const mins = now.getMinutes();
  if (mins > 0 && mins <= 30)  { now.setMinutes(30, 0, 0); }
  else if (mins > 30)          { now.setHours(now.getHours() + 1, 0, 0, 0); }
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const end = new Date(now.getTime() + 60 * 60 * 1000);
  return { ...BLANK_FORM_SHAPE, start_datetime: fmt(now), eind_datetime: fmt(end) };
}

function EventFormModal({ event, editMeta, categories, users, roles, sections, commissions, inviteScope, onClose, onSaved }) {
  const [form, setForm] = useState(() => event ? {
    titel: event.titel || '',
    beschrijving: event.beschrijving || '',
    categorie_id: event.categorie_id || '',
    start_datetime: toInputVal(event.start_datetime),
    eind_datetime: toInputVal(event.eind_datetime),
    hele_dag: !!event.hele_dag,
    locatie_tekst: event.locatie_tekst || '',
    rsvp_actief: !!event.rsvp_actief,
    max_deelnemers: event.max_deelnemers || '',
  } : makeBlankForm());
  const [genodigden, setGenodigden] = useState(
    event?.genodigden?.map(g => ({ scope: g.scope, scope_id: g.scope_id })) || []
  );
  const [rruleVal,   setRruleVal]   = useState(event?.rrule || '');
  const [rruleUntil, setRruleUntil] = useState(event?.rrule_until || '');
  const [reminders,  setReminders]  = useState(event?.reminders || []);
  const [showRepeat,    setShowRepeat]    = useState(!!(event?.rrule));
  const [showReminders, setShowReminders] = useState(!!(event?.reminders?.length));
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-fill end datetime: same day, +1 hour (or same day for all-day)
  function handleStartChange(v) {
    set('start_datetime', v);
    if (!v) return;
    if (form.hele_dag) {
      set('eind_datetime', v); // same day
    } else {
      const start = new Date(v);
      if (isNaN(start)) return;
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      const pad = n => String(n).padStart(2,'0');
      set('eind_datetime', `${end.getFullYear()}-${pad(end.getMonth()+1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`);
    }
  }

  // Only show recurrence editor when creating new or editing 'all'
  const showRecurrence = !editMeta || editMeta.scope === 'all';
  const isEdit = !!(event?.id || editMeta);

  async function handleSave() {
    if (!form.titel.trim() || !form.start_datetime || !form.eind_datetime) {
      setError('Vul titel, start- en eindtijd in.');
      return;
    }
    if (new Date(form.eind_datetime) <= new Date(form.start_datetime)) {
      setError('Eindtijd moet na de starttijd liggen.');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        categorie_id: form.categorie_id || null,
        max_deelnemers: form.max_deelnemers || null,
        genodigden,
        rrule: rruleVal || null,
        rrule_until: rruleUntil || null,
        reminders,
      };

      if (editMeta) {
        // Recurring edit — always PUT to parent id
        if (editMeta.scope !== 'all') {
          payload.scope = editMeta.scope;
          payload._parent_id = editMeta._parent_id;
          payload._occurrence_dt = editMeta._occurrence_dt;
        }
        await api(`/api/calendar/events/${editMeta._parent_id}`, { method:'PUT', body: JSON.stringify(payload) });
      } else if (event?.id) {
        await api(`/api/calendar/events/${event.id}`, { method:'PUT', body: JSON.stringify(payload) });
      } else {
        await api('/api/calendar/events', { method:'POST', body: JSON.stringify(payload) });
      }
      onSaved();
    } catch (err) {
      const known = err.code && translateError(err.code) !== err.code ? translateError(err.code) : null;
      setError(err.detail || known || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen title={isEdit ? 'Event bewerken' : 'Nieuw event'} onClose={onClose} style="max-width:680px;width:95vw">
      <div style="display:flex;flex-direction:column;gap:10px">
        <div class="form-group" style="margin:0">
          <label class="form-label">Titel *</label>
          <input class="form-input" value={form.titel} onInput={e=>set('titel',e.target.value)} placeholder="Naam van het event" autoFocus />
        </div>

        {/* Genodigden */}
        <div class="form-group" style="margin:0">
          <label class="form-label">Genodigden</label>
          <div style="font-size:11px;color:var(--color-text-muted);margin-bottom:6px">
            {/* Without invites only the maker sees the event; a role for some departments can't pick Iedereen */}
            Laat leeg voor een event alleen voor jezelf{inviteScope && !inviteScope.all ? '.' : '; kies Iedereen voor alle clubleden.'}
          </div>
          <InvitePicker
            inviteScope={inviteScope}
            value={genodigden}
            onChange={next => {
              setGenodigden(next);
              if (next.length > 0 && !form.rsvp_actief) set('rsvp_actief', true);
              if (next.length === 0 && form.rsvp_actief) set('rsvp_actief', false);
            }}
            users={users} sections={sections} commissions={commissions}
          />
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div class="form-group" style="margin:0">
            <label class="form-label">Categorie</label>
            <select class="form-input" value={form.categorie_id} onChange={e=>set('categorie_id',e.target.value)}>
              <option value="">— Geen —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.icoon} {c.naam}</option>)}
            </select>
          </div>
          <div class="form-group" style="margin:0;display:flex;align-items:center;gap:8px;padding-top:22px">
            <input type="checkbox" id="heledag" checked={form.hele_dag} onChange={e=>set('hele_dag',e.target.checked)} style="width:16px;height:16px;cursor:pointer;accent-color:var(--color-primary)" />
            <label for="heledag" style="font-size:13px;cursor:pointer">Hele dag</label>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div class="form-group" style="margin:0">
            <label class="form-label">Start *</label>
            <input class="form-input" type={form.hele_dag ? 'date' : 'datetime-local'} value={form.hele_dag ? form.start_datetime?.slice(0,10) : form.start_datetime} onInput={e=>handleStartChange(e.target.value)} />
          </div>
          <div class="form-group" style="margin:0">
            <label class="form-label">Einde event *</label>
            <input class="form-input" type={form.hele_dag ? 'date' : 'datetime-local'} value={form.hele_dag ? form.eind_datetime?.slice(0,10) : form.eind_datetime} onInput={e=>set('eind_datetime', e.target.value)} />
          </div>
        </div>

        <div class="form-group" style="margin:0">
          <label class="form-label">Locatie</label>
          <input class="form-input" value={form.locatie_tekst} onInput={e=>set('locatie_tekst',e.target.value)} placeholder="Adres of naam locatie" />
        </div>

        <div class="form-group" style="margin:0">
          <label class="form-label">Omschrijving</label>
          <textarea class="form-input" rows="3" value={form.beschrijving} onInput={e=>set('beschrijving',e.target.value)} placeholder="Optionele toelichting" style="resize:vertical" />
          <AiAssist kind="event_description" value={form.beschrijving} context={{ title: form.titel }} onUse={text => set('beschrijving', text)} />
        </div>

        <div style="display:flex;gap:16px;align-items:center;padding:10px 14px;border-radius:8px;background:var(--color-bg-secondary);border:1px solid var(--color-border)">
          <div style="display:flex;align-items:center;gap:8px">
            <input type="checkbox" id="rsvp" checked={form.rsvp_actief} onChange={e=>set('rsvp_actief',e.target.checked)} style="width:16px;height:16px;cursor:pointer;accent-color:var(--color-primary)" />
            <label for="rsvp" style="font-size:13px;cursor:pointer;font-weight:500">Aanmelding inschakelen</label>
          </div>
          {form.rsvp_actief && (
            <div style="display:flex;align-items:center;gap:6px;margin-left:auto">
              <label style="font-size:12px;color:var(--color-text-muted)">Max. plekken</label>
              <input class="form-input" type="number" min="1" value={form.max_deelnemers} onInput={e=>set('max_deelnemers',e.target.value)} placeholder="Onbeperkt" style="width:90px;padding:4px 8px;font-size:13px" />
            </div>
          )}
        </div>

        {/* Herhaling */}
        {showRecurrence && (
          <div style="border:1px solid var(--color-border);border-radius:8px;overflow:hidden">
            <button type="button" onClick={() => setShowRepeat(v => !v)}
              style="width:100%;display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:var(--color-bg-secondary);border:none;cursor:pointer;font-size:13px;font-weight:600">
              <span>🔁 Herhaling{rruleVal ? ` (${rruleVal.split(';')[0].replace('FREQ=','').toLowerCase()})` : ''}</span>
              <span style="font-size:16px;color:var(--color-text-muted)">{showRepeat ? '▲' : '▼'}</span>
            </button>
            {showRepeat && (
              <div style="padding:12px 14px">
                <RecurrenceEditor
                  rrule={rruleVal}
                  rruleUntil={rruleUntil}
                  startDate={form.start_datetime}
                  onChange={(r, u) => { setRruleVal(r || ''); setRruleUntil(u || ''); }}
                />
              </div>
            )}
          </div>
        )}

        {/* Herinneringen */}
        <div style="border:1px solid var(--color-border);border-radius:8px;overflow:hidden">
          <button type="button" onClick={() => setShowReminders(v => !v)}
            style="width:100%;display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:var(--color-bg-secondary);border:none;cursor:pointer;font-size:13px;font-weight:600">
            <span>🔔 Herinneringen{reminders.length ? ` (${reminders.length})` : ''}</span>
            <span style="font-size:16px;color:var(--color-text-muted)">{showReminders ? '▲' : '▼'}</span>
          </button>
          {showReminders && (
            <div style="padding:12px 14px">
              <ReminderPicker value={reminders} onChange={setReminders} />
            </div>
          )}
        </div>

        {error && <div class="alert alert-error">{error}</div>}
      </div>

      <div class="flex gap-2 justify-end mt-4">
        <Button variant="secondary" onClick={onClose}>Annuleren</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Opslaan...' : 'Opslaan'}</Button>
      </div>
    </Modal>
  );
}

// ── EventDetailModal ──────────────────────────────────────────────────────────

// The sections and commissions an event is for: in the list the server sends them as
// JSON, the detail (GET /api/calendar/events/:id) has its invitees
function eventTargets(ev) {
  if (Array.isArray(ev.genodigden)) {
    return ev.genodigden.filter(g => g.scope === 'section' || g.scope === 'commission').map(g => ({ type: g.scope, id: g.scope_id }));
  }
  try { return JSON.parse(ev.invite_groups || '[]'); } catch { return []; }
}

export function EventDetailModal({ event, canManage, onClose, onEdit, onDelete, onDeleteClick, onRsvp }) {
  const [rsvpLoading, setRsvpLoading] = useState(false);

  async function handleRsvp(status) {
    setRsvpLoading(true);
    await onRsvp(event.id, status);
    setRsvpLoading(false);
  }

  const cat = event.categorie_kleur;
  const RSVP_BTNS = [
    { status: 'aangemeld', label: '✓ Aanmelden',  color: '#16a34a', bg: '#dcfce7' },
    { status: 'misschien', label: '? Misschien',  color: '#d97706', bg: '#fef3c7' },
    { status: 'afgemeld',  label: '✗ Afmelden',   color: '#dc2626', bg: '#fee2e2' },
  ];

  return (
    <Modal isOpen title="" onClose={onClose} style="max-width:500px;width:95vw">
      {/* Colour header */}
      <div style={`margin:-20px -20px 16px;padding:20px;border-radius:12px 12px 0 0;background:${cat ? cat + '22' : 'color-mix(in srgb, var(--color-primary) 13%, transparent)'};border-bottom:3px solid ${cat || 'var(--color-primary)'}`}>
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px">
          <div style="flex:1">
            {event.categorie_naam && (
              <div style={`font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:${cat};margin-bottom:6px`}>
                {event.categorie_icoon} {event.categorie_naam}
              </div>
            )}
            <h2 style="font-size:20px;font-weight:800;margin:0;line-height:1.2">{event.titel}</h2>
            <DepartmentLabels targets={eventTargets(event)} wrap="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px" />
          </div>
          {/* Why: a meeting's event is managed on the meeting page, so the two never drift apart */}
          {event.meeting_id && (
            <a href={`/meetings/${event.meeting_id}`} onClick={e => { e.preventDefault(); onClose(); route(`/meetings/${event.meeting_id}`); }}
              style="padding:6px 10px;border-radius:6px;border:1px solid var(--color-border);background:var(--color-surface);font-size:12px;color:var(--color-text);text-decoration:none;flex-shrink:0">
              Open vergadering ›
            </a>
          )}
          {/* The server says per event what this member may do (a role for a department:
              only with its events); the role rights are the fallback */}
          {canManage && !event.meeting_id && (event.can_edit !== false || event.can_delete !== false) && (
            <div style="display:flex;gap:6px;flex-shrink:0">
              {event.can_edit !== false && <button onClick={() => onEdit(event)} style="padding:6px 10px;border-radius:6px;border:1px solid var(--color-border);background:var(--color-surface);cursor:pointer;font-size:12px">✏️ Bewerken</button>}
              {event.can_delete !== false && <button onClick={() => (onDeleteClick || onDelete)(event)} style="padding:6px 10px;border-radius:6px;border:1px solid #fca5a5;background:#fee2e2;color:#dc2626;cursor:pointer;font-size:12px">🗑</button>}
            </div>
          )}
        </div>
      </div>

      <div style="display:flex;flex-direction:column;gap:12px">
        {/* Recurring badge */}
        {(event._recurring || event.rrule) && (
          <div style="display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:10px;background:color-mix(in srgb, var(--color-primary) 7%, transparent);border:1px solid color-mix(in srgb, var(--color-primary) 20%, transparent);font-size:11px;font-weight:600;color:var(--color-primary);align-self:flex-start">
            ↻ Herhalend event
          </div>
        )}
        {/* Date/time */}
        <div style="display:flex;gap:8px;align-items:flex-start">
          <span style="font-size:16px;margin-top:1px">📅</span>
          <div>
            <div style="font-size:14px;font-weight:600">{fmtDate(event.start_datetime)}</div>
            {!event.hele_dag && (
              <div style="font-size:13px;color:var(--color-text-muted)">{fmtTime(event.start_datetime)} – {fmtTime(event.eind_datetime)}</div>
            )}
          </div>
        </div>

        {event.locatie_tekst && (
          <div style="display:flex;gap:8px;align-items:center">
            <span style="font-size:16px">📍</span>
            <span style="font-size:14px">{event.locatie_tekst}</span>
          </div>
        )}

        {event.beschrijving && (
          <div style="font-size:13px;color:var(--color-text-muted);line-height:1.6;padding:10px;background:var(--color-bg-secondary);border-radius:8px;white-space:pre-wrap">
            {event.beschrijving}
          </div>
        )}

        {/* RSVP */}
        {/* Why: rsvp_actief comes back as 0/1, and Preact renders `{0 && x}` as "0" */}
        {!!event.rsvp_actief && (
          <div style="border:1px solid var(--color-border);border-radius:10px;padding:14px">
            <div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted);margin-bottom:10px">
              Aanmelding
              {event.max_deelnemers > 0 && <span style="font-weight:400;margin-left:6px">({event.rsvp_aangemeld}/{event.max_deelnemers} plekken)</span>}
              {!(event.max_deelnemers > 0) && <span style="font-weight:400;margin-left:6px">({event.rsvp_aangemeld} aangemeld)</span>}
            </div>
            {event.max_deelnemers > 0 && (
              <div style="height:6px;background:var(--color-border);border-radius:3px;margin-bottom:12px;overflow:hidden">
                <div style={`height:100%;background:${cat || 'var(--color-primary)'};width:${Math.min(100,(event.rsvp_aangemeld/event.max_deelnemers)*100)}%;border-radius:3px;transition:width .3s`} />
              </div>
            )}
            <div style="display:flex;gap:8px">
              {RSVP_BTNS.map(btn => (
                <button
                  key={btn.status}
                  onClick={() => handleRsvp(btn.status)}
                  disabled={rsvpLoading}
                  style={`flex:1;padding:8px 4px;border-radius:8px;border:2px solid ${event.mijn_rsvp === btn.status ? btn.color : 'transparent'};background:${event.mijn_rsvp === btn.status ? btn.bg : 'var(--color-bg-secondary)'};color:${event.mijn_rsvp === btn.status ? btn.color : 'var(--color-text-muted)'};font-size:12px;font-weight:600;cursor:pointer;transition:all .15s`}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Attendees list */}
        {event.deelnemers?.filter(d => d.status === 'aangemeld').length > 0 && (
          <div>
            <div style="font-size:12px;font-weight:600;color:var(--color-text-muted);margin-bottom:6px">Aangemeld</div>
            <div style="display:flex;flex-wrap:wrap;gap:6px">
              {event.deelnemers.filter(d => d.status === 'aangemeld').map(d => (
                <span key={d.id} style="font-size:12px;padding:2px 8px;border-radius:10px;background:var(--color-bg-secondary);border:1px solid var(--color-border)">{d.display_name}</span>
              ))}
            </div>
          </div>
        )}

        {/* Genodigden */}
        {event.genodigden?.length > 0 && (
          <div>
            <div style="font-size:12px;font-weight:600;color:var(--color-text-muted);margin-bottom:6px">Uitgenodigd</div>
            <div style="display:flex;flex-wrap:wrap;gap:6px">
              {event.genodigden.map((g, i) => (
                <span key={i} style="font-size:12px;padding:2px 10px;border-radius:10px;background:color-mix(in srgb, var(--color-primary) 7%, transparent);border:1px solid color-mix(in srgb, var(--color-primary) 20%, transparent);color:var(--color-primary)">
                  {g.scope === 'iedereen' ? '👥 Iedereen'
                    : g.scope === 'rol' ? `🏷 ${g.label}`
                    : g.scope === 'section' ? `🏢 ${g.label}`
                    : g.scope === 'commission' ? `📋 ${g.label}`
                    : g.scope === 'groep' ? `📋 ${g.label}`
                    : `👤 ${g.label}`}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Reminders */}
        {event.reminders?.length > 0 && (
          <div>
            <div style="font-size:12px;font-weight:600;color:var(--color-text-muted);margin-bottom:6px">Herinneringen</div>
            <div style="display:flex;flex-wrap:wrap;gap:6px">
              {event.reminders.map((r, i) => {
                const preset = REMINDER_PRESETS.find(p => p.value === r.trigger_minutes);
                const tl = preset ? preset.label : `${Math.abs(r.trigger_minutes)} min voor`;
                return (
                  <span key={i} style="font-size:12px;padding:2px 10px;border-radius:10px;background:var(--color-bg-secondary);border:1px solid var(--color-border)">
                    {r.type === 'email' ? '📧' : '🔔'} {tl}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px">
          Aangemaakt door {event.aangemaakt_door_naam} · {fmtDate(parseServerDate(event.aangemaakt_op))}
        </div>
      </div>
    </Modal>
  );
}

// ── MiniCalendar ──────────────────────────────────────────────────────────────

function MiniCalendar({ current, onSelect }) {
  const [view, setView] = useState(new Date(current));
  const today = new Date();

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDay = new Date(year, month, 1);
  const startDay = startOfWeek(firstDay);
  const cells = [];
  const d = new Date(startDay);
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }

  return (
    <div style="padding:12px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
        <button onClick={() => setView(v => { const n = new Date(v); n.setMonth(n.getMonth()-1); return n; })}
          style="background:none;border:none;cursor:pointer;padding:2px 6px;border-radius:4px;color:var(--color-text-muted);font-size:14px">‹</button>
        <span style="font-size:12px;font-weight:600">{NL_MONTHS[month].slice(0,3)} {year}</span>
        <button onClick={() => setView(v => { const n = new Date(v); n.setMonth(n.getMonth()+1); return n; })}
          style="background:none;border:none;cursor:pointer;padding:2px 6px;border-radius:4px;color:var(--color-text-muted);font-size:14px">›</button>
      </div>
      <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:1px;text-align:center">
        {NL_DAYS_SHORT.map(d => <div key={d} style="font-size:10px;font-weight:600;color:var(--color-text-muted);padding:2px 0">{d}</div>)}
        {cells.map((cell, i) => {
          const isToday = isSameDay(cell, today);
          const isCurrent = isSameDay(cell, new Date(current));
          const otherMonth = cell.getMonth() !== month;
          return (
            <button
              key={i}
              onClick={() => onSelect(cell)}
              style={`
                font-size:11px;padding:3px;border:none;border-radius:4px;cursor:pointer;
                background:${isCurrent ? 'var(--color-primary)' : isToday ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : 'transparent'};
                color:${isCurrent ? '#fff' : otherMonth ? 'var(--color-border)' : isToday ? 'var(--color-primary)' : 'var(--color-text)'};
                font-weight:${isToday || isCurrent ? '700' : '400'};
              `}
            >{cell.getDate()}</button>
          );
        })}
      </div>
    </div>
  );
}

// ── Month view ────────────────────────────────────────────────────────────────

function MonthView({ date, events, categories, onDayClick, onEventClick }) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const startDay = startOfWeek(firstDay);
  const today = new Date();

  const weeks = [];
  const d = new Date(startDay);
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let day = 0; day < 7; day++) {
      week.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    weeks.push(week);
    if (d.getMonth() !== month && w >= 4) break;
  }

  const eventsOnDay = (day) => events.filter(e => {
    const start = new Date(e.start_datetime);
    const end = new Date(e.eind_datetime);
    return day >= new Date(start.getFullYear(), start.getMonth(), start.getDate()) &&
           day <= new Date(end.getFullYear(), end.getMonth(), end.getDate());
  });

  return (
    <div style="flex:1;display:flex;flex-direction:column;overflow:hidden">
      {/* Day headers */}
      <div style="display:grid;grid-template-columns:repeat(7,1fr);border-bottom:1px solid var(--color-border)">
        {NL_DAYS_SHORT.map(d => (
          <div key={d} style="padding:8px;text-align:center;font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted)">{d}</div>
        ))}
      </div>
      {/* Weeks */}
      <div style="flex:1;display:flex;flex-direction:column;overflow-y:auto">
        {weeks.map((week, wi) => (
          <div key={wi} style="display:grid;grid-template-columns:repeat(7,1fr);flex:1;min-height:110px;border-bottom:1px solid var(--color-border)">
            {week.map((day, di) => {
              const dayEvents = eventsOnDay(day);
              const isToday = isSameDay(day, today);
              const otherMonth = day.getMonth() !== month;
              return (
                <div
                  key={di}
                  onClick={() => onDayClick(day)}
                  style={`padding:5px;border-right:${di<6?'1px solid var(--color-border)':'none'};background:${isToday?'color-mix(in srgb, var(--color-primary) 3%, transparent)':'transparent'};cursor:pointer;min-height:110px;transition:background .1s;overflow:hidden;min-width:0`}
                  onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = 'var(--color-bg-secondary)'; }}
                  onMouseLeave={e => { if (!isToday) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={`
                    width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;
                    font-size:14px;font-weight:${isToday?'800':'500'};margin:2px auto 4px;
                    background:${isToday?'var(--color-primary)':'transparent'};
                    color:${isToday?'#fff':otherMonth?'var(--color-border)':'var(--color-text)'};
                  `}>{day.getDate()}</div>
                  <div style="display:flex;flex-direction:column;gap:2px">
                    {dayEvents.slice(0, 3).map(ev => (
                      <div
                        key={ev.id}
                        onClick={e => { e.stopPropagation(); onEventClick(ev); }}
                        style={`
                          font-size:12px;padding:2px 5px;border-radius:3px;
                          background:${ev.categorie_kleur ? ev.categorie_kleur + '22' : 'color-mix(in srgb, var(--color-primary) 13%, transparent)'};
                          border-left:3px solid ${ev.categorie_kleur || 'var(--color-primary)'};
                          color:var(--color-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
                          cursor:pointer;font-weight:500;
                        `}
                        title={ev.titel}
                      >
                        {!ev.hele_dag && <span style="color:var(--color-text-muted);margin-right:3px">{fmtTime(ev.start_datetime)}</span>}
                        {ev._recurring ? '↻ ' : ''}{ev.titel}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div style="font-size:11px;color:var(--color-primary);font-weight:600;padding-left:4px">+{dayEvents.length-3} meer</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Week view ─────────────────────────────────────────────────────────────────

function WeekView({ date, events, onEventClick }) {
  const weekStart = startOfWeek(date);
  const days = Array.from({length:7}, (_,i) => { const d = new Date(weekStart); d.setDate(d.getDate()+i); return d; });
  const today = new Date();
  const HOURS = Array.from({length:24}, (_,i) => i);

  const eventsForDay = (day) => events.filter(e => {
    if (e.hele_dag) return false;
    return isSameDay(new Date(e.start_datetime), day);
  });
  const allDayForDay = (day) => events.filter(e => e.hele_dag && isSameDay(new Date(e.start_datetime), day));

  function eventTop(e) {
    const s = new Date(e.start_datetime);
    return (s.getHours() * 60 + s.getMinutes()) / 60 * 60;
  }
  function eventHeight(e) {
    const s = new Date(e.start_datetime);
    const en = new Date(e.eind_datetime);
    const mins = (en - s) / 60000;
    return Math.max(20, mins / 60 * 60);
  }

  return (
    <div style="flex:1;display:flex;flex-direction:column;overflow:hidden">
      {/* Day headers */}
      <div style="display:grid;grid-template-columns:48px repeat(7,1fr);border-bottom:1px solid var(--color-border);flex-shrink:0">
        <div />
        {days.map((day, i) => {
          const isToday = isSameDay(day, today);
          return (
            <div key={i} style="padding:8px 4px;text-align:center;border-left:1px solid var(--color-border)">
              <div style="font-size:12px;font-weight:700;text-transform:uppercase;color:var(--color-text-muted)">{NL_DAYS_SHORT[i]}</div>
              <div style={`font-size:18px;font-weight:800;width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:2px auto 0;background:${isToday?'var(--color-primary)':'transparent'};color:${isToday?'#fff':'var(--color-text)'}`}>{day.getDate()}</div>
              {allDayForDay(day).map(ev => (
                <div key={ev.id} onClick={() => onEventClick(ev)} style={`font-size:11px;padding:2px 4px;border-radius:3px;background:${ev.categorie_kleur ? ev.categorie_kleur + '22' : 'color-mix(in srgb, var(--color-primary) 13%, transparent)'};border-left:3px solid ${ev.categorie_kleur||'var(--color-primary)'};margin-top:2px;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap`}>{ev.titel}</div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div style="flex:1;overflow-y:auto">
        <div style="display:grid;grid-template-columns:48px repeat(7,1fr);position:relative">
          {/* Hour labels */}
          <div>
            {HOURS.map(h => (
              <div key={h} style="height:60px;border-bottom:1px solid var(--color-border);display:flex;align-items:flex-start;justify-content:flex-end;padding-right:6px;padding-top:2px">
                <span style="font-size:11px;color:var(--color-text-muted)">{String(h).padStart(2,'0')}:00</span>
              </div>
            ))}
          </div>
          {/* Day columns */}
          {days.map((day, di) => {
            const dayEvs = eventsForDay(day);
            const isToday = isSameDay(day, today);
            const nowMins = today.getHours()*60+today.getMinutes();
            return (
              <div key={di} style={`border-left:1px solid var(--color-border);position:relative;background:${isToday?'color-mix(in srgb, var(--color-primary) 2%, transparent)':'transparent'}`}>
                {HOURS.map(h => <div key={h} style="height:60px;border-bottom:1px solid var(--color-border)" />)}
                {isToday && (
                  <div style={`position:absolute;left:0;right:0;top:${nowMins}px;height:2px;background:var(--color-primary);z-index:2`}>
                    <div style="width:8px;height:8px;border-radius:50%;background:var(--color-primary);margin-top:-3px;margin-left:-4px" />
                  </div>
                )}
                {dayEvs.map(ev => (
                  <div
                    key={ev.id}
                    onClick={() => onEventClick(ev)}
                    style={`
                      position:absolute;left:2px;right:2px;
                      top:${eventTop(ev)}px;height:${eventHeight(ev)}px;
                      background:${ev.categorie_kleur ? ev.categorie_kleur + '22' : 'color-mix(in srgb, var(--color-primary) 13%, transparent)'};
                      border-left:3px solid ${ev.categorie_kleur||'var(--color-primary)'};
                      border-radius:4px;padding:3px 6px;font-size:12px;font-weight:500;
                      cursor:pointer;overflow:hidden;z-index:1;
                    `}
                  >
                    <div style="font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{ev.titel}</div>
                    <div style="font-size:11px;color:var(--color-text-muted)">{fmtTime(ev.start_datetime)}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Agenda view ───────────────────────────────────────────────────────────────

export function AgendaView({ events, onEventClick }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const upcoming = events
    .filter(e => new Date(e.eind_datetime) >= today)
    .sort((a,b) => new Date(a.start_datetime) - new Date(b.start_datetime));

  if (upcoming.length === 0) {
    return (
      <div style="flex:1;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:12px;color:var(--color-text-muted)">
        <div style="font-size:40px">📭</div>
        <div style="font-size:14px">Geen komende evenementen</div>
      </div>
    );
  }

  // Group by date
  const groups = {};
  upcoming.forEach(ev => {
    const key = new Date(ev.start_datetime).toDateString();
    if (!groups[key]) groups[key] = [];
    groups[key].push(ev);
  });

  return (
    <div style="flex:1;overflow-y:auto;padding:16px 0">
      {Object.entries(groups).map(([key, evs]) => {
        const day = new Date(key);
        const isToday = isSameDay(day, new Date());
        return (
          <div key={key} style="display:flex;gap:16px;padding:8px 16px;border-bottom:1px solid var(--color-border)">
            <div style={`width:56px;flex-shrink:0;text-align:center;padding:8px 0`}>
              <div style={`font-size:12px;font-weight:700;text-transform:uppercase;color:${isToday?'var(--color-primary)':'var(--color-text-muted)'}`}>{NL_DAYS_SHORT[(day.getDay()+6)%7]}</div>
              <div style={`font-size:24px;font-weight:800;color:${isToday?'var(--color-primary)':'var(--color-text)'};line-height:1`}>{day.getDate()}</div>
              <div style={`font-size:12px;color:${isToday?'var(--color-primary)':'var(--color-text-muted)'}`}>{NL_MONTHS[day.getMonth()].slice(0,3)}</div>
            </div>
            <div style="flex:1;display:flex;flex-direction:column;gap:6px;padding:4px 0">
              {evs.map(ev => (
                <div
                  key={ev.id}
                  onClick={() => onEventClick(ev)}
                  style={`display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;border:1px solid var(--color-border);background:var(--color-surface);cursor:pointer;transition:border-color .15s`}
                  onMouseEnter={e => e.currentTarget.style.borderColor = ev.categorie_kleur || 'var(--color-primary)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border)'}
                >
                  <div style={`width:4px;height:36px;border-radius:2px;background:${ev.categorie_kleur||'var(--color-primary)'};flex-shrink:0`} />
                  <div style="flex:1;min-width:0">
                    <div style="font-weight:600;font-size:15px">{ev._recurring ? '↻ ' : ''}{ev.titel}</div>
                    <div style="font-size:13px;color:var(--color-text-muted);display:flex;gap:8px;margin-top:2px">
                      {ev.hele_dag ? <span>Hele dag</span> : <span>{fmtTime(ev.start_datetime)} – {fmtTime(ev.eind_datetime)}</span>}
                      {ev.locatie_tekst && <span>📍 {ev.locatie_tekst}</span>}
                      {ev.categorie_naam && <span style={`color:${ev.categorie_kleur}`}>{ev.categorie_icoon} {ev.categorie_naam}</span>}
                      <DepartmentLabels targets={eventTargets(ev)} />
                    </div>
                  </div>
                  {!!ev.rsvp_actief && (
                    <div style="font-size:12px;font-weight:600;padding:2px 8px;border-radius:8px;background:var(--color-bg-secondary);border:1px solid var(--color-border);white-space:nowrap">
                      {ev.rsvp_aangemeld} aangemeld
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Day view ──────────────────────────────────────────────────────────────────

function DayView({ date, events, onEventClick }) {
  const today = new Date();
  const isToday = isSameDay(date, today);
  const HOURS = Array.from({length:24}, (_,i) => i);
  const dayEvs = events.filter(e => !e.hele_dag && isSameDay(new Date(e.start_datetime), date));
  const allDay = events.filter(e => e.hele_dag && isSameDay(new Date(e.start_datetime), date));
  const nowMins = today.getHours()*60+today.getMinutes();

  function eventTop(e) {
    const s = new Date(e.start_datetime);
    return (s.getHours()*60+s.getMinutes())/60*64;
  }
  function eventHeight(e) {
    const s = new Date(e.start_datetime), en = new Date(e.eind_datetime);
    return Math.max(24, (en-s)/60000/60*64);
  }

  return (
    <div style="flex:1;display:flex;flex-direction:column;overflow:hidden">
      <div style="padding:12px 16px;border-bottom:1px solid var(--color-border);flex-shrink:0">
        <div style={`font-size:22px;font-weight:800;color:${isToday?'var(--color-primary)':'var(--color-text)'}`}>
          {NL_DAYS_LONG[(date.getDay()+6)%7]} {date.getDate()} {NL_MONTHS[date.getMonth()]} {date.getFullYear()}
        </div>
        {allDay.map(ev => (
          <div key={ev.id} onClick={() => onEventClick(ev)} style={`display:inline-flex;margin-top:6px;margin-right:6px;padding:2px 10px;border-radius:10px;background:${ev.categorie_kleur ? ev.categorie_kleur + '22' : 'color-mix(in srgb, var(--color-primary) 13%, transparent)'};border:1px solid ${ev.categorie_kleur||'var(--color-primary)'};font-size:12px;cursor:pointer`}>{ev.titel}</div>
        ))}
      </div>
      <div style="flex:1;overflow-y:auto">
        <div style="display:grid;grid-template-columns:48px 1fr;position:relative">
          <div>
            {HOURS.map(h => <div key={h} style="height:64px;border-bottom:1px solid var(--color-border);display:flex;align-items:flex-start;justify-content:flex-end;padding-right:6px;padding-top:2px"><span style="font-size:12px;color:var(--color-text-muted)">{String(h).padStart(2,'0')}:00</span></div>)}
          </div>
          <div style="border-left:1px solid var(--color-border);position:relative">
            {HOURS.map(h => <div key={h} style="height:64px;border-bottom:1px solid var(--color-border)" />)}
            {isToday && <div style={`position:absolute;left:0;right:0;top:${nowMins/60*64}px;height:2px;background:var(--color-primary);z-index:2`}><div style="width:8px;height:8px;border-radius:50%;background:var(--color-primary);margin-top:-3px;margin-left:-4px"/></div>}
            {dayEvs.map(ev => (
              <div key={ev.id} onClick={() => onEventClick(ev)} style={`position:absolute;left:4px;right:4px;top:${eventTop(ev)}px;height:${eventHeight(ev)}px;background:${ev.categorie_kleur ? ev.categorie_kleur + '22' : 'color-mix(in srgb, var(--color-primary) 13%, transparent)'};border-left:3px solid ${ev.categorie_kleur||'var(--color-primary)'};border-radius:4px;padding:4px 8px;font-size:13px;font-weight:500;cursor:pointer;overflow:hidden;z-index:1`}>
                <div style="font-weight:700">{ev.titel}</div>
                <div style="font-size:12px;color:var(--color-text-muted)">{fmtTime(ev.start_datetime)} – {fmtTime(ev.eind_datetime)}</div>
                {ev.locatie_tekst && <div style="font-size:12px;color:var(--color-text-muted)">📍 {ev.locatie_tekst}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main CalendarView ─────────────────────────────────────────────────────────

export default function CalendarView() {
  const auth = useAuth();
  const canManage = auth.hasPermission('calendar_manage', 'create') || auth.hasPermission('settings', 'update');
  const isAdmin   = auth.hasPermission('calendar_admin', 'read') || auth.hasPermission('settings', 'update');

  const [view, setView]       = useState('month'); // month | week | day | agenda
  const [current, setCurrent] = useState(new Date());
  const [events, setEvents]   = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategories, setActiveCategories] = useState(null); // null = all
  const [showAll, setShowAll] = useState(isAdmin); // admins see all events by default
  const [showSubscribe, setShowSubscribe] = useState(false);

  const [detailEvent, setDetailEvent] = useState(null);
  const [formEvent, setFormEvent]     = useState(null); // null=closed, {}=new, {id,...}=edit
  const [editMeta, setEditMeta]       = useState(null); // {scope, _parent_id, _occurrence_dt}
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [scopeDialog, setScopeDialog] = useState(null); // {event, action:'edit'|'delete'}

  // Invite picker data
  const [inviteUsers,       setInviteUsers]       = useState([]);
  const [inviteRoles,       setInviteRoles]        = useState([]);
  const [inviteSections,    setInviteSections]    = useState([]);
  const [inviteCommissions, setInviteCommissions] = useState([]);
  const [inviteScope, setInviteScope] = useState(null);

  // Load categories + invite picker data once
  useEffect(() => {
    api('/api/calendar/categories')
      .then(data => { setCategories(data); setActiveCategories(data.map(c => c.id)); })
      .catch(err => console.error('Failed to load categories:', err));
    api('/api/calendar/users').then(setInviteUsers).catch(err => console.error('Failed to load invite users:', err));
    api('/api/calendar/roles').then(setInviteRoles).catch(err => console.error('Failed to load invite roles:', err));
    api('/api/calendar/sections-commissions').then(d => { setInviteSections(d.sections); setInviteCommissions(d.commissions); }).catch(err => console.error('Failed to load sections/commissions:', err));
    if (canManage || isAdmin) api('/api/calendar/invite-scope').then(setInviteScope).catch(() => {});
  }, []);

  // Load events for visible range. Admins can pass all=1 to see all club events.
  useEffect(() => {
    const { start, end } = getRange();
    setLoading(true);
    const allParam = (isAdmin && showAll) ? '&all=1' : '';
    api(`/api/calendar/events?start=${start.toISOString()}&end=${end.toISOString()}${allParam}`)
      .then(setEvents)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [current, view, showAll]);

  function getRange() {
    const d = new Date(current);
    if (view === 'month') {
      const start = new Date(d.getFullYear(), d.getMonth()-1, 1);
      const end = new Date(d.getFullYear(), d.getMonth()+2, 1);
      return { start, end };
    } else if (view === 'week') {
      const start = startOfWeek(d);
      const end = new Date(start); end.setDate(end.getDate()+7);
      return { start, end };
    } else if (view === 'day') {
      const start = new Date(d); start.setHours(0,0,0,0);
      const end = new Date(d); end.setHours(23,59,59,999);
      return { start, end };
    } else {
      // agenda: next 90 days
      const start = new Date(); start.setHours(0,0,0,0);
      const end = new Date(start); end.setDate(end.getDate()+90);
      return { start, end };
    }
  }

  function navigate(dir) {
    const d = new Date(current);
    if (view === 'month')  d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir*7);
    else if (view === 'day')  d.setDate(d.getDate() + dir);
    setCurrent(d);
  }

  function periodLabel() {
    if (view === 'month') return `${NL_MONTHS[current.getMonth()]} ${current.getFullYear()}`;
    if (view === 'week') {
      const ws = startOfWeek(current);
      const we = new Date(ws); we.setDate(we.getDate()+6);
      return `${ws.getDate()} – ${we.getDate()} ${NL_MONTHS[we.getMonth()]} ${we.getFullYear()}`;
    }
    if (view === 'day') return `${NL_DAYS_LONG[(current.getDay()+6)%7]} ${current.getDate()} ${NL_MONTHS[current.getMonth()]} ${current.getFullYear()}`;
    return 'Agenda';
  }

  const filteredEvents = events.filter(e => {
    if (activeCategories !== null && e.categorie_id && !activeCategories.includes(e.categorie_id)) return false;
    return true;
  });

  async function handleRsvp(eventId, status) {
    try {
      const result = await api(`/api/calendar/events/${eventId}/rsvp`, { method:'POST', body: JSON.stringify({ status }) });
      setEvents(evs => evs.map(e => e.id === eventId ? { ...e, mijn_rsvp: status, rsvp_aangemeld: result.rsvp_aangemeld } : e));
      if (detailEvent?.id === eventId) setDetailEvent(prev => ({ ...prev, mijn_rsvp: status, rsvp_aangemeld: result.rsvp_aangemeld }));
    } catch (err) {
      alert(err?.error === 'event_full' ? 'Dit event is vol — aanmelden is niet meer mogelijk.' : 'Aanmelding mislukt, probeer het opnieuw.');
    }
  }

  async function openDetail(ev) {
    // A meeting of the gebruikersoverleg of the club's sportcomplex: opened in the club
    if (ev.complex_meeting_id) { route(`/complex/overleg/${ev.complex_meeting_id}`); return; }
    try {
      // For virtual recurring occurrences use the parent id so we always get
      // a real DB row (with reminders, genodigden, etc.)
      // A moved occurrence is a real row (numeric id): show its own title, place and
      // reminders, with the series' invitees (they belong to the series).
      const isMovedRow = ev._recurring && ev._parent_id && /^\d+$/.test(String(ev.id));
      const fetchId = ev._recurring && ev._parent_id && !isMovedRow ? ev._parent_id : ev.id;
      const full = await api(`/api/calendar/events/${fetchId}`);
      if (isMovedRow) {
        const series = await api(`/api/calendar/events/${ev._parent_id}`).catch(() => null);
        if (series) {
          if (!full.genodigden?.length) full.genodigden = series.genodigden;
          if (!full.reminders?.length) full.reminders = series.reminders;
          full.rrule = series.rrule;
          full.rrule_until = series.rrule_until;
        }
      }
      if (ev._recurring) {
        // Overlay the occurrence-specific timing + recurring metadata so the
        // detail modal shows the right date and edit/delete handlers work
        setDetailEvent({
          ...full,
          start_datetime:  ev.start_datetime,
          eind_datetime:   ev.eind_datetime,
          _recurring:      1,
          _parent_id:      ev._parent_id,
          _occurrence_dt:  ev._occurrence_dt,
        });
      } else {
        setDetailEvent(full);
      }
    } catch { setDetailEvent(ev); }
  }

  async function handleDelete() {
    if (!deleteConfirm) return;
    try {
      await api(`/api/calendar/events/${deleteConfirm.id}`, { method:'DELETE' });
      setDeleteConfirm(null); setDetailEvent(null);
      reloadEvents();
    } catch {
      alert('Verwijderen mislukt, probeer het opnieuw.');
    }
  }

  function reloadEvents() {
    const { start, end } = getRange();
    const allParam = (isAdmin && showAll) ? '&all=1' : '';
    api(`/api/calendar/events?start=${start.toISOString()}&end=${end.toISOString()}${allParam}`)
      .then(setEvents)
      .catch(err => console.error('Failed to reload events:', err));
  }

  function handleSaved() {
    setFormEvent(null);
    setEditMeta(null);
    reloadEvents();
    if (detailEvent) setDetailEvent(null);
  }

  function handleFormClose() {
    setFormEvent(null);
    setEditMeta(null);
  }

  // Edit click — if recurring occurrence, show scope dialog first
  function handleEditClick(ev) {
    setDetailEvent(null);
    if (ev._recurring) {
      setScopeDialog({ event: ev, action: 'edit' });
    } else {
      setFormEvent(ev);
    }
  }

  // Delete click — if recurring occurrence, show scope dialog first
  function handleDeleteClick(ev) {
    setDetailEvent(null);
    if (ev._recurring) {
      setScopeDialog({ event: ev, action: 'delete' });
    } else {
      setDeleteConfirm(ev);
    }
  }

  // Scope dialog confirmed
  async function handleScopeSelected(scope) {
    const { event, action } = scopeDialog;
    setScopeDialog(null);

    if (action === 'edit') {
      if (scope === 'all') {
        // openDetail already loaded the full parent — strip the occurrence overlay
        const { _recurring, _parent_id, _occurrence_dt, start_datetime, eind_datetime, ...parentData } = event;
        // Restore original master datetimes from the full object (openDetail put occurrence datetimes on top)
        // Re-fetch to get the master's own start/end reliably
        try {
          const parent = await api(`/api/calendar/events/${event._parent_id}`);
          setEditMeta({ scope: 'all', _parent_id: event._parent_id, _occurrence_dt: event._occurrence_dt });
          setFormEvent(parent);
        } catch {
          // Fallback: use what we have (minus occurrence overlay)
          setEditMeta({ scope: 'all', _parent_id: event._parent_id, _occurrence_dt: event._occurrence_dt });
          setFormEvent({ ...parentData, start_datetime: event.start_datetime, eind_datetime: event.eind_datetime });
        }
      } else {
        setEditMeta({ scope, _parent_id: event._parent_id, _occurrence_dt: event._occurrence_dt });
        setFormEvent(event);
      }
    } else {
      // Delete
      try {
        if (scope === 'all') {
          await api(`/api/calendar/events/${event._parent_id}`, { method:'DELETE' });
        } else {
          const qs = `scope=${scope}&_parent_id=${event._parent_id}&_occurrence_dt=${encodeURIComponent(event._occurrence_dt)}`;
          await api(`/api/calendar/events/${event._parent_id}?${qs}`, { method:'DELETE' });
        }
        reloadEvents();
      } catch {
        alert('Verwijderen mislukt.');
      }
    }
  }

  const VIEWS = [
    { key:'month',  label:'Maand' },
    { key:'week',   label:'Week' },
    { key:'day',    label:'Dag' },
    { key:'agenda', label:'Agenda' },
  ];

  const toggleCategory = (id) => {
    setActiveCategories(prev => {
      if (prev === null) return categories.map(c => c.id).filter(c => c !== id);
      return prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id];
    });
  };

  return (
    <div style="position:fixed;top:var(--header-height);left:var(--sidebar-width);right:0;bottom:0;display:flex;overflow:hidden;background:var(--color-bg)">

      {/* ── Left sidebar — hidden on mobile ── */}
      <style>{`
        @media (max-width: 640px) {
          .cal-sidebar { display: none !important; }
          .cal-sidebar.open { display: flex !important; position:absolute;top:0;left:0;bottom:0;z-index:20;background:var(--color-bg);box-shadow:2px 0 12px rgba(0,0,0,.15); }
          .cal-sidebar-toggle { display: flex !important; }
        }
        @media (min-width: 641px) { .cal-sidebar-toggle { display: none !important; } }
      `}</style>
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style="position:absolute;inset:0;z-index:19;background:rgba(0,0,0,.3)" />}
      <div class={`cal-sidebar${sidebarOpen ? ' open' : ''}`} style="width:220px;flex-shrink:0;border-right:1px solid var(--color-border);display:flex;flex-direction:column;overflow-y:auto">
        <MiniCalendar current={current} onSelect={d => { setCurrent(d); setView('day'); }} />

        <div style="padding:12px;border-top:1px solid var(--color-border)">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:8px">Categorieën</div>
          <div style="display:flex;flex-direction:column;gap:6px">
            {categories.map(cat => {
              const active = activeCategories === null || activeCategories.includes(cat.id);
              return (
                <button key={cat.id} onClick={() => toggleCategory(cat.id)} style={`display:flex;align-items:center;gap:8px;padding:5px 8px;border-radius:6px;border:none;background:${active?cat.kleur+'18':'transparent'};cursor:pointer;text-align:left;transition:background .1s`}>
                  <div style={`width:10px;height:10px;border-radius:50%;background:${active?cat.kleur:'var(--color-border)'};flex-shrink:0;border:2px solid ${cat.kleur};transition:background .15s`} />
                  <span style="font-size:12px;font-weight:500">{cat.icoon} {cat.naam}</span>
                </button>
              );
            })}
          </div>
        </div>

        {isAdmin && (
          <div style="padding:12px;border-top:1px solid var(--color-border)">
            <button onClick={() => setShowAll(v => !v)} style={`display:flex;align-items:center;gap:8px;padding:5px 8px;border-radius:6px;border:none;background:${showAll?'color-mix(in srgb, var(--color-primary) 10%, transparent)':'transparent'};cursor:pointer;width:100%;text-align:left`}>
              <div style={`width:10px;height:10px;border-radius:50%;border:2px solid var(--color-primary);background:${showAll?'var(--color-primary)':'transparent'}`} />
              <span style="font-size:12px;font-weight:500">Alle evenementen</span>
            </button>
          </div>
        )}

      </div>

      {/* ── Main area ── */}
      <div style="flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0">

        {/* Topbar */}
        <div style="display:flex;align-items:center;gap:8px;padding:10px 16px;border-bottom:1px solid var(--color-border);flex-shrink:0;flex-wrap:wrap">
          <button class="cal-sidebar-toggle" onClick={() => setSidebarOpen(v => !v)} style="display:none;padding:6px 10px;border-radius:6px;border:1px solid var(--color-border);background:var(--color-surface);cursor:pointer;font-size:16px;line-height:1">☰</button>
          <div style="display:flex;gap:4px">
            <button onClick={() => navigate(-1)} style="padding:6px 10px;border-radius:6px;border:1px solid var(--color-border);background:var(--color-surface);cursor:pointer;font-size:14px">‹</button>
            <button onClick={() => setCurrent(new Date())} style="padding:6px 12px;border-radius:6px;border:1px solid var(--color-border);background:var(--color-surface);cursor:pointer;font-size:12px;font-weight:600">Vandaag</button>
            <button onClick={() => navigate(1)} style="padding:6px 10px;border-radius:6px;border:1px solid var(--color-border);background:var(--color-surface);cursor:pointer;font-size:14px">›</button>
          </div>

          <h2 style="font-size:16px;font-weight:700;margin:0;flex:1;min-width:120px">{periodLabel()}</h2>

          {/* View switcher */}
          <div style="display:flex;border:1px solid var(--color-border);border-radius:8px;overflow:hidden">
            {VIEWS.map(v => (
              <button key={v.key} onClick={() => setView(v.key)} style={`padding:6px 12px;border:none;cursor:pointer;font-size:12px;font-weight:600;background:${view===v.key?'var(--color-primary)':'var(--color-surface)'};color:${view===v.key?'#fff':'var(--color-text-muted)'};transition:all .15s`}>{v.label}</button>
            ))}
          </div>

          <button onClick={() => setShowSubscribe(true)} title="Zet je afspraken in Outlook, Google of je telefoon" style="padding:6px 12px;border-radius:8px;border:1px solid var(--color-border);background:var(--color-surface);cursor:pointer;font-size:12px;font-weight:600;white-space:nowrap">📅 In je agenda</button>

          {canManage && (
            <button
              onClick={() => setFormEvent({})}
              style="padding:7px 14px;border-radius:8px;border:none;background:var(--color-primary);color:#fff;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap"
            >
              + Nieuw event
            </button>
          )}
        </div>

        {/* Calendar view */}
        {loading ? (
          <div style="flex:1;display:flex;align-items:center;justify-content:center;color:var(--color-text-muted)">Laden...</div>
        ) : view === 'month' ? (
          <MonthView date={current} events={filteredEvents} categories={categories} onDayClick={d => { setCurrent(d); setView('day'); }} onEventClick={openDetail} />
        ) : view === 'week' ? (
          <WeekView date={current} events={filteredEvents} onEventClick={openDetail} />
        ) : view === 'day' ? (
          <DayView date={current} events={filteredEvents} onEventClick={openDetail} />
        ) : (
          <AgendaView events={filteredEvents} onEventClick={openDetail} />
        )}
      </div>

      {/* ── Modals ── */}
      {showSubscribe && <SubscribeModal onClose={() => setShowSubscribe(false)} />}
      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          canManage={canManage || isAdmin}
          onClose={() => setDetailEvent(null)}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onDeleteClick={handleDeleteClick}
          onRsvp={handleRsvp}
        />
      )}

      {formEvent !== null && (
        <EventFormModal
          event={formEvent?.id ? formEvent : null}
          editMeta={editMeta}
          categories={categories}
          users={inviteUsers}
          roles={inviteRoles}
          sections={inviteSections}
          commissions={inviteCommissions}
          inviteScope={inviteScope}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {scopeDialog && (
        <RecurrenceScopeDialog
          mode={scopeDialog.action}
          onSelect={handleScopeSelected}
          onClose={() => setScopeDialog(null)}
        />
      )}

      {deleteConfirm && (
        <Modal isOpen title="Event verwijderen" onClose={() => setDeleteConfirm(null)} style="max-width:400px">
          <p style="color:var(--color-text-muted);margin-bottom:16px">
            Weet je zeker dat je <strong>{deleteConfirm.titel}</strong> wilt verwijderen?
          </p>
          <div class="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Annuleren</Button>
            <Button variant="danger" onClick={handleDelete}>Verwijderen</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
